import { useChainId } from '@occc-app/Chain/hooks';
import { useGetOfferById } from '@occc-app/dataAccessLayer/offer/queries';
import { useMemo } from 'react';
import { useGetCampaignsOffer } from '@occc-app/dataAccessLayer/campaign/queries';
import { CampaignFragment, OfferFragment } from '@occc/graphql';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';
import { useGetAudienceById } from '@occc-app/dataAccessLayer/audience/useGetAudienceById';
import { prepareOffer } from './component-details.utils';
import { useConstraintSupplementalData } from './use-constraint-supplemental-data.hook';

const useOfferHook = (offer?: OfferFragment | null, campaign?: CampaignFragment | null) => {
  const chainId = useChainId();
  const isComponentFromDifferentChain = campaign ? campaign?.chain.id !== chainId : false;

  const { constraintSupplementalData, productGroups, loading } = useConstraintSupplementalData({
    campaignId: campaign?.uuid || '',
    chainId,
    constraints: offer?.constraints || [],
    isComponentFromDifferentChain,
  });

  const inAudience = offer?.constraints?.filter(constraintHelper.isInAudience)?.[0];
  const { loading: audienceLoading, data: audience } = useGetAudienceById(
    inAudience?.audienceId,
    chainId,
  );

  return useMemo(() => {
    return {
      offer:
        !loading && !audienceLoading && offer
          ? prepareOffer(offer, productGroups, audience)
          : undefined,
      campaign,
      loading: loading || audienceLoading,
      constraintSupplementalData,
      isComponentFromDifferentChain,
    };
  }, [
    offer,
    campaign,
    loading,
    productGroups,
    constraintSupplementalData,
    isComponentFromDifferentChain,
    audience,
    audienceLoading,
  ]);
};

export const usePreparedCampaignOfferHook = (offerId: string, campaignId: string) => {
  const { loading, error, data } = useGetCampaignsOffer(campaignId, offerId);
  const { loading: restDataLoading, ...restData } = useOfferHook(data?.offer, data?.campaign);

  return {
    ...restData,
    error,
    loading: loading || restDataLoading,
  };
};

export const usePreparedOfferHook = (offerId: string) => {
  const chainId = useChainId();
  const { loading, error, data } = useGetOfferById(offerId, chainId);
  const { loading: restDataLoading, ...restData } = useOfferHook(data);

  return {
    ...restData,
    error,
    loading: loading || restDataLoading,
  };
};
